import React, { useState } from "react";
import DateTimePicker from "./DateTimePicker";

/**
 * チケットタイプ
 */
type TicketType = {
	/** フロントエンド用の一時ID */
	id: string;
	external_id?: string;
	name: string;
	description: string;
	isFree: boolean;
	price: number;
	hasLimit: boolean;
	quantity: number;
	publicationDateTime: string;
	saleStartDateTime: string;
	saleEndDateTime: string;
	/** OpeningTime の外部IDリスト */
	openingTimeIds: string[];
	soldTickets: number;
};

/**
 * 開場・開演時間
 */
type OpeningTime = {
	id: string;
	external_id?: string; // バックエンドで管理されるexternal_id
	openTime: string;
	startTime: string;
	endTime: string;
	description?: string;
};

type TicketTypesProps = {
	ticketTypes: TicketType[];
	openingTimes: OpeningTime[];
	handleTicketTypeChange: (
		id: string,
		field: keyof TicketType,
		value: any
	) => void;
	addTicketType: () => void;
	removeTicketType: (id: string) => void;
	toggleOpeningTimeForTicket: (ticketId: string, openingTimeId: string) => void;
};

const TicketTypes: React.FC<TicketTypesProps> = ({
	ticketTypes,
	openingTimes,
	handleTicketTypeChange,
	addTicketType,
	removeTicketType,
	toggleOpeningTimeForTicket,
}) => {
	const [expandedTickets, setExpandedTickets] = useState<
		Record<string, boolean>
	>({});

	const toggleExpand = (ticketId: string) => {
		setExpandedTickets((prev) => ({
			...prev,
			[ticketId]: !prev[ticketId],
		}));
	};

	return (
		<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
			<div className="flex justify-between items-center">
				<h3 className="text-lg mb-4 font-semibold text-gray-900 dark:text-white pb-2">
					チケット種別
				</h3>
				<button
					type="button"
					onClick={addTicketType}
					className="text-blue-600 hover:text-blue-800 dark:text-violet-400 dark:hover:text-violet-300 flex items-center text-sm"
				>
					チケットを追加
				</button>
			</div>

			<div className="space-y-4">
				{ticketTypes.map((ticket) => {
					const hasPurchases = ticket.soldTickets > 0;
					const quantityFloor = Math.max(1, ticket.soldTickets || 0);
					return (
						<div
							key={ticket.id}
							className="border rounded-lg dark:border-gray-700 bg-gray-50 dark:bg-gray-700"
						>
							{/* Accordion Header */}
							<div
								className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 cursor-pointer"
								onClick={() => toggleExpand(ticket.id)}
								aria-expanded={expandedTickets[ticket.id] ? "true" : "false"}
								aria-controls={`ticket-content-${ticket.id}`}
							>
								<div className="flex-grow">
									<div>
										<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
											チケット名{" "}
											<span className="text-red-600 dark:text-red-400">*</span>
										</label>
										<input
											type="text"
											value={ticket.name}
											onChange={(e) =>
												handleTicketTypeChange(
													ticket.id,
													"name",
													e.target.value
												)
											}
											placeholder="チケット名を入力"
											maxLength={50}
											required
											className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:focus:ring-violet-400 dark:placeholder-gray-500"
											onClick={(e) => e.stopPropagation()}
										/>
										<div className="mt-1 flex items-center">
											<div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
												<div
													className="bg-blue-600 h-2 rounded-full"
													style={{
														width: `${(ticket.name.length / 50) * 100}%`,
													}}
												></div>
											</div>
											<p className="text-xs text-gray-500 dark:text-gray-400 ml-2 whitespace-nowrap">
												{ticket.name.length}/50文字
											</p>
										</div>
									</div>
								</div>

								<div className="flex items-center ml-4 space-x-2">
									{hasPurchases && (
										<span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full dark:bg-amber-400/20 dark:text-amber-100">
											購入済み {ticket.soldTickets} 枚
										</span>
									)}
									{expandedTickets[ticket.id] ? (
										<svg
											className="w-5 h-5 text-gray-500 dark:text-gray-400"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth="2"
												d="M20 12H4"
											></path>
										</svg>
									) : (
										<svg
											className="w-5 h-5 text-gray-500 dark:text-gray-400"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth="2"
												d="M12 4v16m8-8H4"
											></path>
										</svg>
									)}
								</div>

								{ticketTypes.length > 1 && !hasPurchases && (
									<button
										onClick={(e) => {
											e.stopPropagation();
											removeTicketType(ticket.id);
										}}
										type="button"
										className="ml-2 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
										title="このチケットを削除"
									>
										<svg
											className="w-5 h-5"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth="2"
												d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
											></path>
										</svg>
									</button>
								)}
							</div>

							{/* Accordion Content */}
							<div
								id={`ticket-content-${ticket.id}`}
								className={`transition-all duration-300 ease-in-out ${
									expandedTickets[ticket.id]
										? "max-h-[2000px] opacity-100"
										: "max-h-0 opacity-0 overflow-hidden"
								}`}
							>
								<div className="p-4 space-y-4 border-t dark:border-gray-700">
									<div>
										<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
											説明
										</label>
										<textarea
											value={ticket.description}
											onChange={(e) =>
												handleTicketTypeChange(
													ticket.id,
													"description",
													e.target.value
												)
											}
											maxLength={500}
											rows={2}
											className="w-full px-3 sm:px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:focus:ring-violet-400 dark:placeholder-gray-500"
										/>
										<div className="mt-1 flex items-center">
											<div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
												<div
													className="bg-blue-600 h-2.5 rounded-full"
													style={{
														width: `${
															(ticket.description.length / 500) * 100
														}%`,
													}}
												></div>
											</div>
											<p className="text-xs text-gray-500 dark:text-gray-400 ml-2 whitespace-nowrap">
												{ticket.description.length}/500文字
											</p>
										</div>
									</div>

									<div>
										<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
											価格設定{" "}
											<span className="text-red-600 dark:text-red-400">*</span>
										</label>
										<div className="flex items-center space-x-4">
											<div>
												<label
													htmlFor={`free-${ticket.id}`}
													className={`flex cursor-pointer items-center justify-center rounded-md border border-gray-100 bg-white px-3 py-2.5 h-[42px] text-gray-900 hover:border-gray-200 has-[:checked]:border-blue-600 has-[:checked]:bg-blue-600 has-[:checked]:text-white dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:border-gray-500 dark:has-[:checked]:border-violet-500 dark:has-[:checked]:bg-violet-500 ${
														hasPurchases ? "opacity-60 cursor-not-allowed" : ""
													}`}
												>
													<input
														type="radio"
														name={`price-type-${ticket.id}`}
														value="free"
														id={`free-${ticket.id}`}
														className="sr-only"
														disabled={hasPurchases}
														checked={ticket.isFree}
														onChange={() =>
															handleTicketTypeChange(ticket.id, "isFree", true)
														}
													/>
													<p className="text-sm font-medium">無料</p>
												</label>
											</div>

											<div>
												<label
													htmlFor={`paid-${ticket.id}`}
													className={`flex cursor-pointer items-center justify-center rounded-md border border-gray-100 bg-white px-3 py-2.5 h-[42px] text-gray-900 hover:border-gray-200 has-[:checked]:border-blue-600 has-[:checked]:bg-blue-600 has-[:checked]:text-white dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:border-gray-500 dark:has-[:checked]:border-violet-500 dark:has-[:checked]:bg-violet-500 ${
														hasPurchases ? "opacity-60 cursor-not-allowed" : ""
													}`}
												>
													<input
														type="radio"
														name={`price-type-${ticket.id}`}
														value="paid"
														id={`paid-${ticket.id}`}
														className="sr-only"
														disabled={hasPurchases}
														checked={!ticket.isFree}
														onChange={() =>
															handleTicketTypeChange(ticket.id, "isFree", false)
														}
													/>
													<p className="text-sm font-medium">有料</p>
												</label>
											</div>

											{!ticket.isFree && (
												<div className="flex items-center">
													<input
														type="number"
														value={ticket.price}
														onChange={(e) =>
															handleTicketTypeChange(
																ticket.id,
																"price",
																parseInt(e.target.value, 10)
															)
														}
														min={1}
														max={300000}
														className={`w-28 px-3 sm:px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:focus:ring-violet-400 dark:placeholder-gray-500 ${
															hasPurchases
																? "bg-gray-100 cursor-not-allowed"
																: ""
														}`}
														required={true}
														disabled={hasPurchases}
													/>
													<span className="ml-2 dark:text-gray-300">
														円<span className="text-xs">（税込）</span>
													</span>
												</div>
											)}
										</div>

										{!ticket.isFree && (
											<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
												1〜300,000円の範囲で設定
											</p>
										)}
									</div>

									<div>
										<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
											販売枚数{" "}
											<span className="text-red-600 dark:text-red-400">*</span>
										</label>
										<div className="flex items-center space-x-4">
											<div>
												<label
													htmlFor={`unlimited-${ticket.id}`}
													className="flex cursor-pointer items-center justify-center rounded-md border border-gray-100 bg-white px-3 py-2.5 h-[42px] text-gray-900 hover:border-gray-200 has-[:checked]:border-blue-600 has-[:checked]:bg-blue-600 has-[:checked]:text-white dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:border-gray-500 dark:has-[:checked]:border-violet-500 dark:has-[:checked]:bg-violet-500"
												>
													<input
														type="radio"
														name={`quantity-type-${ticket.id}`}
														value="unlimited"
														id={`unlimited-${ticket.id}`}
														className="sr-only"
														checked={!ticket.hasLimit}
														onChange={() =>
															handleTicketTypeChange(
																ticket.id,
																"hasLimit",
																false
															)
														}
													/>
													<p className="text-sm font-medium">制限なし</p>
												</label>
											</div>

											<div>
												<label
													htmlFor={`limited-${ticket.id}`}
													className="flex cursor-pointer items-center justify-center rounded-md border border-gray-100 bg-white px-3 py-2.5 h-[42px] text-gray-900 hover:border-gray-200 has-[:checked]:border-blue-600 has-[:checked]:bg-blue-600 has-[:checked]:text-white dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:border-gray-500 dark:has-[:checked]:border-violet-500 dark:has-[:checked]:bg-violet-500"
												>
													<input
														type="radio"
														name={`quantity-type-${ticket.id}`}
														value="limited"
														id={`limited-${ticket.id}`}
														className="sr-only"
														checked={ticket.hasLimit}
														onChange={() =>
															handleTicketTypeChange(
																ticket.id,
																"hasLimit",
																true
															)
														}
													/>
													<p className="text-sm font-medium">制限あり</p>
												</label>
											</div>

											{ticket.hasLimit && (
												<div className="flex items-center">
													<input
														type="number"
														value={ticket.quantity}
														onChange={(e) =>
															handleTicketTypeChange(
																ticket.id,
																"quantity",
																parseInt(e.target.value, 10)
															)
														}
														min={ticket.hasLimit ? quantityFloor : 1}
														max={100000}
														className="w-28 px-3 sm:px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:focus:ring-violet-400 dark:placeholder-gray-500"
														required={true}
													/>
													<span className="ml-2 dark:text-gray-300">枚</span>
												</div>
											)}
										</div>

										{ticket.hasLimit && (
											<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
												1〜100,000枚の範囲で設定
												{hasPurchases && (
													<span className="ml-1 text-amber-600 dark:text-amber-300">
														最低 {quantityFloor} 枚
													</span>
												)}
											</p>
										)}
									</div>

									<div>
										<DateTimePicker
											id={`ticket-publication-date-time-${ticket.id}`}
											label="公開日時（任意）"
											value={ticket.publicationDateTime}
											onChange={(value: string) =>
												handleTicketTypeChange(
													ticket.id,
													"publicationDateTime",
													value
												)
											}
											placeholder="公開日時を選択"
											disabled={hasPurchases}
										/>
									</div>

									<div>
										<DateTimePicker
											id={`ticket-sale-start-date-time-${ticket.id}`}
											label="販売開始日時（任意）"
											value={ticket.saleStartDateTime}
											onChange={(value: string) =>
												handleTicketTypeChange(
													ticket.id,
													"saleStartDateTime",
													value
												)
											}
											placeholder="販売開始日時を選択"
											disabled={hasPurchases}
										/>
									</div>

									<div>
										<DateTimePicker
											id={`ticket-sale-end-date-time-${ticket.id}`}
											label="販売終了日時（任意）"
											value={ticket.saleEndDateTime}
											onChange={(value: string) =>
												handleTicketTypeChange(
													ticket.id,
													"saleEndDateTime",
													value
												)
											}
											placeholder="販売終了日時を選択"
										/>
									</div>

									<div>
										<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
											対象の開場/開演情報{" "}
											<span className="text-red-600 dark:text-red-400">*</span>
										</label>
										<div>
											{openingTimes.length === 0 ? (
												<p className="text-gray-500 dark:text-gray-400">
													開場/開演情報を追加してください
												</p>
											) : (
												<div className="space-y-2">
													{openingTimes.map((time) => {
														// 開場時間の参照IDとして external_id を優先使用
														const openingTimeRefId =
															time.external_id || time.id;
														return (
															<label
																key={time.id}
																htmlFor={`time-${ticket.id}-${time.id}`}
																className={`flex p-3 w-full bg-white border border-gray-200 rounded text-sm dark:bg-gray-800 dark:border-gray-600 ${
																	ticket.openingTimeIds.includes(
																		openingTimeRefId
																	)
																		? "text-gray-900 dark:text-white"
																		: "text-gray-500 dark:text-gray-400"
																} ${
																	hasPurchases
																		? "opacity-60 cursor-not-allowed"
																		: ""
																}`}
															>
																<div className="flex items-center h-5 relative">
																	<input
																		type="checkbox"
																		id={`time-${ticket.id}-${time.id}`}
																		name={`time-${ticket.id}-${time.id}`}
																		checked={ticket.openingTimeIds.includes(
																			openingTimeRefId
																		)}
																		onChange={() =>
																			toggleOpeningTimeForTicket(
																				ticket.id,
																				time.id
																			)
																		}
																		className="w-4 h-4 border border-gray-300 rounded-sm bg-gray-50 focus:ring-3 focus:ring-blue-300 checked:bg-blue-600 checked:border-blue-600 appearance-none relative dark:border-gray-600 dark:bg-gray-700 dark:focus:ring-violet-400 dark:checked:bg-violet-500 dark:checked:border-violet-500"
																		disabled={hasPurchases}
																	/>
																	{ticket.openingTimeIds.includes(
																		openingTimeRefId
																	) && (
																		<div className="absolute inset-0 flex items-center justify-center pointer-events-none">
																			<svg
																				className="w-3 h-3 text-white"
																				fill="none"
																				stroke="currentColor"
																				viewBox="0 0 24 24"
																				xmlns="http://www.w3.org/2000/svg"
																			>
																				<path
																					strokeLinecap="round"
																					strokeLinejoin="round"
																					strokeWidth="3"
																					d="M5 13l4 4L19 7"
																				/>
																			</svg>
																		</div>
																	)}
																</div>
																<span
																	className={`text-sm ms-3 ${
																		ticket.openingTimeIds.includes(
																			openingTimeRefId
																		)
																			? "text-gray-900 dark:text-white"
																			: "text-gray-500 dark:text-gray-400"
																	}`}
																>
																	開場: {time.openTime || "未設定"} / 開演:{" "}
																	{time.startTime || "未設定"} / 終演:{" "}
																	{time.endTime || "未設定"}
																	{time.description
																		? ` (${time.description})`
																		: ""}
																</span>
															</label>
														);
													})}
												</div>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default TicketTypes;
